'use client';

import { memo, useCallback, useRef, useState } from 'react';
import classNames from 'classnames';

import hasAudioTrack from '@/utils/hasAudioTrack';

import PosterImage from './PosterImage';
import VideoPlayer from '@/components/VideoPlayer';
import type { LazyVideoProps } from './types';
import { useVideoAutoplay } from '@/hooks/useVideoAutoPlay';
import { PlaybackMode } from '@/types/media';
import { TEST_IDS } from './constants/testIds';
import useStyles from './styles';

const LazyVideo = ({
  src,
  style,
  poster,
  priority = false,
  title,
  width,
  height,
  className,
  mediaClassName,
  sizes,
  playbackMode = PlaybackMode.Scheduled,
  enableAudioControls = false,
}: LazyVideoProps) => {
  const classes = useStyles();
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isMuted, setIsMuted] = useState(true);

  useVideoAutoplay({ containerRef, videoRef, playbackMode });

  const hasAudioControl = Boolean(enableAudioControls && videoRef.current && hasAudioTrack(videoRef.current));
  const handleAudioToggle = useCallback(() => {
    const video = videoRef.current;
    if (!video || !hasAudioControl) {
      return;
    }

    const shouldMute = !video.muted;
    video.muted = shouldMute;
    setIsMuted(shouldMute);
  }, [hasAudioControl]);

  const handleVideoLoadedData = useCallback(() => {
    setIsVideoLoaded(true);
  }, []);

  const showPoster = poster && !isVideoLoaded;

  return (
    <div ref={containerRef} className={classNames('group', classes.root, className)} style={{ aspectRatio: `${width}/${height}`, ...style }} data-testid={TEST_IDS.VIDEO}>
      {showPoster && <PosterImage title={title} width={width} height={height} poster={poster} priority={priority} className={mediaClassName} sizes={sizes} />}

      <VideoPlayer
        videoRef={videoRef}
        src={src}
        width={width}
        height={height}
        videoLabel={title ? `Preview of ${title}` : 'Preview of template video'}
        className={classNames(classes.player, mediaClassName)}
        muted={isMuted}
        onAudioToggle={hasAudioControl ? handleAudioToggle : undefined}
        onLoadedData={handleVideoLoadedData}
      />
    </div>
  );
};

export default memo(LazyVideo);
